import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Banknote, Smartphone, Building2 } from "lucide-react";
import { usePaymentMethods, PaymentMethod } from "@/hooks/usePaymentMethods";

interface PaymentMethodSelectorProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const getPaymentMethodIcon = (methodName: string) => {
  const name = methodName.toLowerCase();
  
  if (name.includes('efectivo')) {
    return <Banknote className="w-4 h-4 text-green-600" />;
  } else if (name.includes('tarjeta')) {
    return <CreditCard className="w-4 h-4 text-blue-600" />;
  } else if (name.includes('bizum')) {
    return <Smartphone className="w-4 h-4 text-purple-600" />;
  } else if (name.includes('transferencia') || name.includes('domiciliación')) {
    return <Building2 className="w-4 h-4 text-orange-600" />;
  }
  
  return <CreditCard className="w-4 h-4 text-gray-600" />;
};

export const PaymentMethodSelector = ({ 
  value, 
  onValueChange, 
  placeholder = "Seleccionar método de pago",
  disabled = false,
  className = ""
}: PaymentMethodSelectorProps) => {
  const { paymentMethods, loading, error } = usePaymentMethods();

  if (loading) {
    return (
      <Select disabled>
        <SelectTrigger className={className}>
          <SelectValue placeholder="Cargando métodos de pago..." />
        </SelectTrigger>
      </Select>
    );
  }

  if (error) {
    return (
      <Select disabled>
        <SelectTrigger className={className}>
          <SelectValue placeholder="Error al cargar métodos de pago" />
        </SelectTrigger>
      </Select>
    );
  }

  const selectedMethod = paymentMethods.find(method => method.id === value);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder}>
          {selectedMethod && (
            <div className="flex items-center gap-2">
              {getPaymentMethodIcon(selectedMethod.name)}
              <span>{selectedMethod.name}</span>
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-background border z-50">
        {paymentMethods.map((method) => (
          <SelectItem key={method.id} value={method.id}>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                {getPaymentMethodIcon(method.name)}
                <span>{method.name}</span>
              </div>
              {method.description && (
                <Badge variant="outline" className="text-xs ml-2">
                  {method.description}
                </Badge>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default PaymentMethodSelector;